import { isValid, parse } from 'date-fns';
import * as XLSX from 'xlsx';
import { getProcessExcelConfig } from 'utils/processExcelConfigs';
import type { ColumnDef, ProcessExcelConfig } from 'utils/processExcelConfigs';

export type RowLog = {
  accountName: string,
  success: boolean,
  errors: string[],
  rowIndex: number,
};

/** Una fila del payload a enviar al API; la forma depende del operationType */
export type ProcessRowPayload = Record<string, unknown>;

export type ProcessExcelResult = {
  rowLogs: RowLog[],
  hasErrors: boolean,
  dataToSend: ProcessRowPayload[],
  /** ID de promoción de la primera fila con datos (columna promotionColIndex) */
  promotionId?: string,
};

function validateString( value: unknown, columnLabel: string ): string | null {
  if ( value === undefined || value === null ) {
    return `${columnLabel}: no puede estar vacía`;
  }
  const s = String( value ).trim();
  if ( s === '' ) {
    return `${columnLabel}: no puede estar vacía`;
  }
  return null;
}

function normalizeStringCell( raw: unknown, def: Pick<ColumnDef, 'stripSpaces'> ): string {
  let s = String( raw ?? '' ).trim();
  if ( def.stripSpaces ) {
    s = s.replace( /\s+/g, '' );
  }
  return s;
}

function validateOptionalStringColumn( raw: unknown, def: ColumnDef ): string | null {
  const label = def.label ?? 'Campo';
  const s = normalizeStringCell( raw, def );
  if ( s === '' ) {
    return null;
  }
  if ( def.alphanumericOnly && !/^[A-Za-z0-9]+$/.test( s ) ) {
    return `${label}: solo se permiten letras y números`;
  }
  if ( def.allowedLengths?.length ) {
    if ( !def.allowedLengths.includes( s.length ) ) {
      const opts = def.allowedLengths.join( ', ' );
      return `${label}: la longitud debe ser ${opts} caracteres`;
    }
  } else {
    if ( def.minLength != null && s.length < def.minLength ) {
      return `${label}: debe tener al menos ${def.minLength} caracteres`;
    }
    if ( def.maxLength != null && s.length > def.maxLength ) {
      return `${label}: no puede superar ${def.maxLength} caracteres`;
    }
  }
  return null;
}

function validateNumeric( value: unknown, columnLabel: string ): string | null {
  if ( value === undefined || value === null ) {
    return `${columnLabel}: debe ser solo números, valor vacío es incorrecto`;
  }
  const s = String( value ).trim();
  if ( s === '' ) {
    return `${columnLabel}: debe ser solo números, valor vacío es incorrecto`;
  }
  const n = Number( s );
  if ( Number.isNaN( n ) ) {
    return `${columnLabel}: con valor "${s}" es incorrecto, debe ser solo números`;
  }
  if ( !/^-?\d*\.?\d+$/.test( s ) ) {
    return `${columnLabel}: con valor "${s}" es incorrecto, debe ser solo números`;
  }
  return null;
}

const DATE_FORMATS = [
  { pattern: /^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/, format: 'yyyy-MM-dd', normalize: ( str: string ) => str.split( '/' ).join( '-' ) },
  { pattern: /^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/, format: 'dd/MM/yyyy', normalize: ( str: string ) => str },
  { pattern: /^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/, format: 'MM/dd/yyyy', normalize: ( str: string ) => str },
] as const;

function validateDate( value: unknown, columnLabel: string ): string | null {
  if ( value === undefined || value === null ) {
    return `${columnLabel}: debe ser una fecha válida, valor vacío es incorrecto`;
  }
  const s = String( value ).trim();
  if ( s === '' ) {
    return `${columnLabel}: debe ser una fecha válida, valor vacío es incorrecto`;
  }
  if ( value instanceof Date ) {
    if ( !isValid( value ) ) {
      return `${columnLabel}: fecha no válida`;
    }
    return null;
  }
  if ( typeof value === 'number' && !Number.isNaN( value ) ) {
    const date = new Date( ( value - 25569 ) * 86400 * 1000 );
    if ( !isValid( date ) ) {
      return `${columnLabel}: fecha no válida`;
    }
    return null;
  }
  const trimmed = s;
  const parsedOk = DATE_FORMATS.some( ( { pattern, format, normalize } ) => {
    if ( !pattern.test( trimmed ) ) return false;
    const toParse = format === 'yyyy-MM-dd' ? normalize( trimmed ) : trimmed;
    try {
      const parsed = parse( toParse, format, new Date() );
      return isValid( parsed );
    } catch {
      return false;
    }
  } );
  if ( parsedOk ) return null;
  if ( /^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test( trimmed ) ) {
    return `${columnLabel}: fecha no válida (mes 1-12, día válido para el mes)`;
  }
  if ( /^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test( trimmed ) ) {
    return `${columnLabel}: fecha no válida (mes 1-12, día válido para el mes)`;
  }
  return `${columnLabel}: formato de fecha no válido. Use YYYY-MM-DD, YYYY/MM/DD, DD/MM/YYYY o MM/DD/YYYY`;
}

function getSheetRows( workbook: XLSX.WorkBook ): unknown[][] {
  const name = workbook.SheetNames[0];
  const sheet = workbook.Sheets[name];
  if ( !sheet ) return [];
  return XLSX.utils.sheet_to_json( sheet, {
    header: 1,
    defval: '',
    raw: true,
  } ) as unknown[][];
}

function isRowEmpty( row: unknown[] ): boolean {
  return row.every( ( cell ) => {
    const v = cell != null ? String( cell ).trim() : '';
    return v === '';
  } );
}

function parseNum( row: unknown[], index: number ): number {
  const s = String( row[index] ?? '' ).trim();
  if ( s === '' ) return 0;
  const n = Number( s );
  return Number.isNaN( n ) ? 0 : n;
}

function getStr( row: unknown[], index: number ): string {
  return String( row[index] ?? '' ).trim();
}

/** Convierte celda de fecha (Excel serial, Date o string) a YYYY-MM-DD */
function getDateStr( row: unknown[], index: number ): string {
  const cell = row[index];
  if ( cell == null ) return '';
  if ( typeof cell === 'number' && !Number.isNaN( cell ) ) {
    const date = new Date( ( cell - 25569 ) * 86400 * 1000 );
    const y = date.getUTCFullYear();
    const m = String( date.getUTCMonth() + 1 ).padStart( 2, '0' );
    const d = String( date.getUTCDate() ).padStart( 2, '0' );
    return `${y}-${m}-${d}`;
  }
  if ( cell instanceof Date ) {
    const y = cell.getFullYear();
    const m = String( cell.getMonth() + 1 ).padStart( 2, '0' );
    const d = String( cell.getDate() ).padStart( 2, '0' );
    return `${y}-${m}-${d}`;
  }
  const s = String( cell ).trim();
  if ( /^\d{4}-\d{2}-\d{2}$/.test( s ) ) return s;
  const parsed = new Date( s );
  if ( !Number.isNaN( parsed.getTime() ) ) {
    const y = parsed.getFullYear();
    const m = String( parsed.getMonth() + 1 ).padStart( 2, '0' );
    const d = String( parsed.getDate() ).padStart( 2, '0' );
    return `${y}-${m}-${d}`;
  }
  return s;
}

function buildRowPayloadFromConfig(
  row: unknown[],
  config: ProcessExcelConfig,
): ProcessRowPayload {
  const { payloadColumns } = config;
  return Object.entries( payloadColumns ).reduce<ProcessRowPayload>(
    ( acc, [key, def] ) => {
      const { colIndex, type } = def;
      let value: string | number;
      if ( type === 'string' ) {
        value = normalizeStringCell( row[colIndex], def );
      } else if ( type === 'number' ) value = parseNum( row, colIndex );
      else value = getDateStr( row, colIndex );
      return { ...acc, [key]: value };
    },
    {},
  );
}

function collectValidationErrors(
  row: unknown[],
  config: ProcessExcelConfig,
): string[] {
  const { payloadColumns } = config;
  const numericErrors = Object.entries( payloadColumns )
    .filter( ( [, def] ) => def.type === 'number' && def.label )
    .map( ( [, def] ) => validateNumeric( row[def.colIndex], def.label! ) )
    .filter( ( msg ): msg is string => msg != null );
  const dateErrors = Object.entries( payloadColumns )
    .filter( ( [, def] ) => def.type === 'date' && def.label )
    .map( ( [, def] ) => validateDate( row[def.colIndex], def.label! ) )
    .filter( ( msg ): msg is string => msg != null );
  const stringErrors = Object.entries( payloadColumns )
    .filter( ( [, def] ) => def.type === 'string' )
    .map( ( [key, def] ) => {
      if ( def.optional ) {
        return validateOptionalStringColumn( row[def.colIndex], def );
      }
      return validateString( row[def.colIndex], def.label ?? key );
    } )
    .filter( ( msg ): msg is string => msg != null );
  return [...numericErrors, ...dateErrors, ...stringErrors];
}

export type ProcessExcelOptions = {
  /** Tipo de operación; define columnas y forma del payload */
  operationType: string,
};

/**
 * Procesa el Excel según la configuración del operationType.
 * Valida columnas numéricas, fecha y string definidas en la config.
 */
export async function processExcelTemplate(
  file: File,
  options: ProcessExcelOptions,
): Promise<ProcessExcelResult> {
  const { operationType } = options;

  const config = getProcessExcelConfig( operationType );
  if ( !config ) {
    return {
      rowLogs: [
        {
          accountName: 'Error',
          success: false,
          errors: [`Tipo de operación "${operationType}" no tiene configuración de Excel`],
          rowIndex: 0,
        },
      ],
      hasErrors: true,
      dataToSend: [],
    };
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read( buffer, { type: 'array' } );
  const rows = getSheetRows( workbook );
  if ( rows.length < 2 ) {
    return {
      rowLogs: [],
      hasErrors: false,
      dataToSend: [],
    };
  }

  const dataRows = rows.slice( 1 );
  const accountNameCol = config.accountNameColIndex ?? 1;
  const promotionCol = config.promotionColIndex;

  const firstDataRow = dataRows[0] as unknown[] | undefined;
  const promotionId = firstDataRow
    ? getStr( firstDataRow, promotionCol )
    : undefined;

  const processed = dataRows
    .map( ( row, i ) => ( { row: row as unknown[], rowIndex: i + 2 } ) )
    .filter( ( { row } ) => !isRowEmpty( row ) )
    .map( ( { row, rowIndex } ) => {
      const accountName = String( row[accountNameCol] ?? '' ).trim()
        || `Fila ${rowIndex}`;
      const rowData = buildRowPayloadFromConfig( row, config );
      const errors = collectValidationErrors( row, config );
      return {
        log: {
          accountName,
          success: errors.length === 0,
          errors,
          rowIndex,
        },
        rowData,
      };
    } );

  const rowLogs = processed.map( ( p ) => p.log );
  const dataToSend = processed.map( ( p ) => p.rowData );
  const hasErrors = rowLogs.some( ( r ) => !r.success );
  return {
    rowLogs,
    hasErrors,
    dataToSend,
    promotionId,
  };
}
